import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Row, Col, Collapse, Overlay } from 'react-bootstrap';
import VoltasPopover from './VoltasPopover';
import styles, { COLORS, getStatusCfg, getRowTone } from './styles';
import { COLS, GRID_TEMPLATE, GRID_GAP, MIN_TOTAL_PADDED } from '../Service/gridConfig';

const ClockIcon = ({ color = '#546e7a', size = 18, style }) => (
  <svg width={size} height={size} style={style} viewBox="0 0 24 24" aria-hidden focusable="false">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" fill="none" />
    <path d="M12 6v6l4 2" stroke={color} strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const CRITICAL_MINUTES = 180;
const HOUR_COLOR_NORMAL = '#666666';
const HOUR_COLOR_CRITICAL = '#d32f2f';

const norm = (s) =>
  (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const pickStageEntry = (v) => {
  if (Array.isArray(v)) {
    return v.find(x => x?.current || x?.active || x?.inProgress) ?? v[v.length - 1] ?? {};
  }
  return v || {};
};

const getDotColor = (info = {}, headerStage = {}) => {
  if (info.color) return info.color;
  const s = norm(
    info.status ||
    info.tag ||
    headerStage.status ||
    headerStage.tag ||
    headerStage.result ||
    ''
  );

  if (s.includes('reprov'))       return COLORS.fail;
  if (s.includes('reval'))        return COLORS.warn;
  if (s.includes('validado') ||
      s.includes('aprov'))        return COLORS.done;
  if (s.includes('em valida') ||
      s.includes('andamento'))    return COLORS.doing;
  if (s.includes('não inici') ||
      s.includes('nao inici'))    return COLORS.none;

  return COLORS.none;
};

const timeTextToMinutes = (txt) => {
  if (!txt) return 0;
  const s = String(txt).toLowerCase();

  const d = /(\d+)\s*d/.exec(s);
  const h = /(\d+)\s*h/.exec(s);
  const m = /(\d+)\s*m/.exec(s);

  const days  = d ? parseInt(d[1], 10) : 0;
  const hours = h ? parseInt(h[1], 10) : 0;
  const mins  = m ? parseInt(m[1], 10) : 0;

  return (days * 24 * 60) + (hours * 60) + mins;
};

function TravelIdLink({ id, text, isFs }) {
  const href = `/viagens/${id}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        color: '#2b19ca',
        textDecoration: 'underline',
        fontWeight: 600,
        display: 'inline-block',
        ...(isFs ? { fontSize: 14, lineHeight: '18px' } : null),
      }}
      title={`Abrir viagem #${id}`}
    >
      {text}
    </a>
  );
}

export default function TravelRow({ item, isFs = false }) {
  const BIG_FS_KEYS = useMemo(() => new Set([
    'viagem',
    'tempoAtividade',
    'tempoFila',
    'idadeViagem',
    'cliente',
    'centroDeCusto',
    'filial',
  ]), []);
  const [open, setOpen] = useState(false);
  const h = item.header || {};
  const toggle = useCallback(() => setOpen(o => !o), []);

  const displayStatusTag = useMemo(() => {
    const apiStatus =
      String(item?.status || h?.travelStatus || h?.status || '')
        .toUpperCase();
    if (apiStatus === 'CANCELLED') return 'Cancelado';
    return h?.statusTag;
  }, [item?.status, h?.travelStatus, h?.status, h?.statusTag]);

  const rawTone = useMemo(() => getRowTone(displayStatusTag), [displayStatusTag]);

  const tone = useMemo(
    () => (h?.highlight ? { ...rawTone, border: '#f6d7b5' } : rawTone),
    [rawTone, h?.highlight]
  );

  const statusChipStyle = useMemo(
    () => styles.statusChipDetail(item?.details?.statusChip?.color || '#90a4ae'),
    [item]
  );

  const timesByLabel = useMemo(
    () => Object.fromEntries((item.details?.temposPorSetor || []).map(t => [t.label, t])),
    [item]
  );

  const stageKeyByLabel = useMemo(() => {
    const m = new Map();
    (COLS || []).forEach(c => {
      if (c.kind === 'stage' && c.label && c.key) m.set(norm(c.label), c.key);
    });
    return m;
  }, []);

  const toArray = (val) => (Array.isArray(val) ? val : (val == null ? [] : [val]));

  const template = useMemo(() => {
    if (Array.isArray(COLS) && COLS.length) {
      return COLS.map(c => `minmax(${c.min ?? 70}px, ${c.fr ?? 1}fr)`).join(' ');
    }
    return GRID_TEMPLATE;
  }, []);

  return (
    <div style={styles.rowCard(tone, open)}>
      <div style={styles.leftStripe(tone)} />
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), toggle())}
        aria-expanded={open}
        aria-label="Expandir detalhes da viagem"
        style={{
          ...styles.headerGrid,
          display: 'grid',
          gridTemplateColumns: template,
          columnGap: GRID_GAP,
          boxSizing: 'border-box',
          padding: '0 16px',
          minWidth: MIN_TOTAL_PADDED,
          width: `max(100%, ${MIN_TOTAL_PADDED}px)`,
          alignItems: 'center'
        }}
      >
        {COLS.map((col, idx) => {
          const baseStyle = {
            ...styles.cell(col.align),
            ...(col.kind === 'stage' ? styles.stageCell : null),
            ...(col.key === 'cliente' ? styles.clientWrap : null),
            ...(col.key === 'centroDeCusto' ? styles.costCenterWrap : null),
            ...(col.key === 'filial' ? styles.filialWrap : null),
            ...(col.key === 'veiculos' ? styles.vehicleWrap : null),
            ...(idx > 0 ? styles.cellDivider : null),
            ...(isFs && BIG_FS_KEYS.has(col.key) ? styles.fsCellBump : null),
            ...(idx >= COLS.length - 4 ? { fontWeight: 400 } : null),
            padding: '0 6px',
            position: 'relative',
          };

          const content = (() => {
            if (col.kind === 'stage') {
              const info = timesByLabel[col.label] || {};
              const headerStage = pickStageEntry(h[col.key]);
              const dotColor   = getDotColor(info, headerStage);
              const mins       = timeTextToMinutes(info.time);
              const isCritical = mins >= CRITICAL_MINUTES;
              const hasVoltas  = info.hasPendency;
              const timeColor = isCritical ? HOUR_COLOR_CRITICAL : HOUR_COLOR_NORMAL;

              const title = [
                info.time || '—',
                hasVoltas ? 'com volta' : null,
                isCritical ? 'crítico > 3h' : null,
              ].filter(Boolean).join(' • ');

              return (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', rowGap: isFs ? 12 : 8 }}>
                    <div style={{ ...styles.trackDotRow(isFs) }}>
                      <span style={styles.dot(dotColor, isFs)} />
                    </div>
                    <div style={styles.timeWrap(isFs)} title={title}>
                      {hasVoltas && (
                        <ClockIcon
                          color="#fa8006"
                          size={isFs ? 16 : 12}
                          style={styles.tinyClock(isFs)}
                        />
                      )}
                      <div style={styles.time(timeColor, isFs)}>{info.time || '—'}</div>
                    </div>
                  </div>
                </>
              );
            }

            if (col.key === 'driverAndShipping') {
              const transportadora = h.driverAndShipping?.transportadora || '-';
              const motorista = h.driverAndShipping?.motorista || '-';

              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  <span>
                    <strong>Transportadora:</strong> {transportadora}
                  </span>

                  <span>
                    <strong>Motorista:</strong> {motorista}
                  </span>
                </div>
              );
            }

            if (col.kind === 'status') {
              const tag = displayStatusTag;
              const cfg = getStatusCfg(tag);

              if (!tag) return '—';
              if (cfg?.variant === 'outline') {
                return <span style={styles.statusPillOutline(cfg, isFs)}>{tag}</span>;
              }
              return <span style={styles.statusPill(cfg.bg, cfg.color, cfg.radius, isFs)}>{tag}</span>;
            }

            if (col.key === 'ordemFila') {
              const n = h.ordemFila;
              const hasValue = n !== undefined && n !== null && String(n).trim() !== '';
              const display = hasValue ? n : '—';

              return (
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: isFs ? 44 : 36,
                    padding: isFs ? '6px 10px' : '4px 8px',
                    borderRadius: 8,
                    color: '#263238',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                  title={hasValue ? 'Posição atual na fila' : 'Sem posição definida'}
                >
                  {display}
                </span>
              );
            }

            if (col.key === 'viagem') {
              const travelId = h.viagem ?? String(item.id ?? '')
              const travelStatus = h.attendancesStatus?.name?.toUpperCase() || '—'
              return (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                >
                  <span>
                    <TravelIdLink id={item.id} text={travelId} isFs={isFs} />
                  </span>

                  <span style={{ fontWeight: 400 }}>
                    {travelStatus}
                  </span>
                </div>
              )
            }

            const value = h[col.key];
            return value ?? '—';
          })();

          return (
            <div key={col.key} style={baseStyle} title={String(h[col.key] ?? '')}>
              {idx > 0 && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: -0.5,
                    top: '12%',
                    bottom: '12%',
                    width: 1,
                    background: '#e3e9f0',
                    pointerEvents: 'none'
                  }}
                />
              )}
              {content}
            </div>
          );
        })}
      </div>

      <div style={styles.divider} />

      <Collapse in={open}>
        <div style={styles.detailsWrap} onClick={(e) => e.stopPropagation()}>
          <div style={styles.details}>
            <span aria-hidden className="d-none d-md-block" style={styles.vDivider} />
            <Row>
              <Col md={6} style={styles.colLeft}>
                <div style={styles.sectionHeader}>
                  <ClockIcon />
                  <div style={styles.sectionTitle}>Tempos por Setor</div>
                  <span style={styles.sectionPill}>Validações Simultâneas</span>
                </div>

                {(item.details?.temposPorSetor || []).map((t, i) => {
                  const key = stageKeyByLabel.get(norm(t.label));
                  const headerStage = key ? pickStageEntry(h[key]) : {};
                  const c = getDotColor(t, headerStage);

                  const historico = Array.isArray(t.historicoVoltas)
                    ? t.historicoVoltas
                    : Array.isArray(t.historico)
                      ? t.historico
                      : [];

                  const canOpen = historico.length > 0;
                  const isCrit = timeTextToMinutes(t.time) >= CRITICAL_MINUTES;

                  return (
                    <div key={i} style={styles.listRow}>
                      <div style={styles.listLabel}>
                        <span style={styles.dotSm(c, isFs)} />
                        {t.label}
                        {historico.length > 0 && (
                          canOpen ? (
                            <VoltasButton
                              i={i}
                              color={c}
                              voltasNum={historico.length}
                              historico={historico}
                            />
                          ) : (
                            <span
                              style={
                                c === COLORS.fail
                                  ? styles.voltaPill('#fdecef', '#d32f2f')
                                  : styles.voltaPill('#e8f5e9', '#2e7d32')
                              }
                            >
                              {`${historico.length}x ${historico.length == 1 ? 'volta' : 'voltas'}`}
                            </span>
                          )
                        )}
                      </div>
                      <div
                        style={{
                          ...styles.listValue,
                          color: isCrit ? HOUR_COLOR_CRITICAL : HOUR_COLOR_NORMAL,
                          fontWeight: isCrit ? 600 : 400,
                        }}
                      >
                        {t.time ?? '—'}
                      </div>
                    </div>
                  );
                })}
              </Col>

              <Col md={6} style={styles.colRight}>
                <div style={styles.sectionHeader}>
                  <div style={styles.sectionTitle}>Informações Adicionais</div>
                </div>

                {(item.details?.informacoesAdicionais || []).map((info, i) => (
                  <div key={i} style={styles.listRow}>
                    <div style={styles.listLabel}>{info.label}</div>
                    <div style={styles.listValue}>{info.value}</div>
                  </div>
                ))}

                {item.details?.statusChip?.text && (
                  <div style={{ marginTop: 10 }}>
                    <span style={statusChipStyle}>{item.details.statusChip.text}</span>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        </div>
      </Collapse>
    </div>
  );
}

function VoltasButton({ i, color, voltasNum, historico }) {
  const btnRef = useRef(null);
  const [show, setShow] = useState(false);

  const pillStyle = color === COLORS.warn
    ? styles.voltaPill('#fdecef', '#d32f2f')
    : styles.voltaPill('#e8f5e9', '#2e7d32');

  const getFsContainer = () => {
    const doc = btnRef.current?.ownerDocument;
    const fsEl = doc?.fullscreenElement || doc?.webkitFullscreenElement;
    return fsEl || btnRef.current?.closest('.tv-fs-root') || doc?.body;
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={show}
        onClick={(e) => {
          e.stopPropagation();
          setShow(s => !s);
        }}
        style={{ ...pillStyle, border: 'none', cursor: 'pointer' }}
      >
        {voltasNum}x voltas
      </button>

      <Overlay
        target={btnRef.current}
        show={show}
        placement="right"
        container={getFsContainer()}
        transition={false}
        rootClose
        onHide={() => setShow(false)}
        popperConfig={{ strategy: 'fixed' }}
      >
        {(props) => (
          <VoltasPopover
            {...props}
            style={{ ...props.style, zIndex: 9999 }}
            popoverId={`voltas-popover-${i}`}
            historico={historico}
            requestClose={() => setShow(false)}
          />
        )}
      </Overlay>
    </>
  );
}
